import { useEffect, useMemo, useRef } from 'react'

import { useEditor } from '@craftjs/core'

import { blocks as BLOCKS } from '@/components/builder/blocks'
import { serializeNodeTree } from '@/components/builder/utils/serialize-block'
import { snapshotNode, snapshotPage } from '@/components/builder/utils/snapshot-block'
import { useBuilderStore } from '@/components/builder/utils/store'
import { uploadBlockPreview } from '@/components/builder/utils/upload-preview'
import { useAutoSave, useBuilder, useCompiler, useResponsive } from '@/modules/cms/views/post/components/builder/context'
import { fetchFontOptions } from '@/modules/cms/views/post/components/builder/hooks/useFont'

import { LIMITS } from '../protocol'
import { createRuntime } from '../runtime'
import { sleep } from '../timers'

let fontsPromise = null

const loadFonts = () => {
  if (!fontsPromise) {
    fontsPromise = fetchFontOptions().then(options => options || []).catch(() => {
      fontsPromise = null
      return []
    })
  }

  return fontsPromise
}

const resolveFontUrl = async name => (await loadFonts()).find(font => font.value === name)?.url || null

const snapshot = (dom, nodeId) => {
  if (!dom || document.visibilityState === 'hidden') {
    return Promise.resolve(null)
  }

  return Promise.race([
    snapshotNode(dom, nodeId).catch(() => null),
    sleep(LIMITS.snapshotTimeout).then(() => null),
  ])
}

export function readSandboxCss() {
  const iframe = document.querySelector('#builder-content iframe')
  const sandbox = iframe?.contentDocument

  if (!sandbox) {
    return ''
  }

  return [...sandbox.querySelectorAll('style:not([type="text/tailwindcss"])')]
    .map(style => style.textContent || '')
    .join('\n')
}

const CSS_POLL = 50
const CSS_QUIET = 100

const cssSize = () => readSandboxCss().length

const cssSettled = async () => {
  const deadline = Date.now() + LIMITS.screenshotCssSettle
  let size = cssSize()
  let quietSince = Date.now()

  while (Date.now() < deadline) {
    await sleep(CSS_POLL)
    const current = cssSize()
    if (current !== size) {
      size = current
      quietSince = Date.now()
    } else if (Date.now() - quietSince >= CSS_QUIET) {
      return
    }
  }
}

const screenshotDom = (store, nodeId) => {
  const dom = store.query.getNodes()[nodeId]?.dom
  return nodeId === 'ROOT' ? dom?.firstElementChild : dom
}

export default function useAgentRuntime() {
  const { store } = useEditor()
  const { resolver, engine } = useCompiler()
  const { handleSave } = useBuilder()
  const { markSaved } = useAutoSave()
  const { device, deviceHeight } = useResponsive()
  const {
    templateBlock: { fetchBlockById },
    block: { save: saveBlockEntry },
    blocks: { reset: resetBlocks },
    siteSelected,
    fetch,
  } = useBuilderStore()

  const latest = useRef({})
  latest.current = {
    save: async () => {
      await handleSave()
      markSaved()
    },
    fetchSavedBlock: async id => {
      const block = await fetchBlockById(id)
      if (!block) {
        return null
      }
      return { definition: block.definition, version: block.meta?.version, name: block.name }
    },
    getHtml: root => engine.buildHTML(store.query.getSerializedNodes(), root).html,
    uploadPreview: blob => uploadBlockPreview(fetch, blob, { [siteSelected.key]: siteSelected.id }),
    saveBlock: async ({ nodeId, name, description, level, tags }) => {
      const definition = serializeNodeTree(store.query.node, nodeId)
      const dom = store.query.getNodes()[nodeId]?.dom
      const image = await snapshot(dom, nodeId)
      const file = image ? await latest.current.uploadPreview(image) : null

      const saved = await saveBlockEntry({
        name,
        definition,
        meta: { file, description },
        tags,
        level,
      })

      resetBlocks()
      return { blockId: saved?.id ?? null, name, level, tags, preview: file?.url ?? null }
    },
    screenshot: async ({ nodeId, scale }) => {
      const dom = screenshotDom(store, nodeId)
      if (!dom) {
        return null
      }

      await cssSettled()

      const effectiveScale = scale ?? Math.min(1, LIMITS.screenshotMaxWidth / (dom.offsetWidth || LIMITS.screenshotMaxWidth))
      const shot = await snapshotPage(dom, { sliceHeight: Math.round(deviceHeight), scale: effectiveScale })

      return { node: nodeId, device, ...shot }
    },
  }

  const helpers = useMemo(() => ({
    save: () => latest.current.save(),
    fetchSavedBlock: id => latest.current.fetchSavedBlock(id),
    saveBlock: options => latest.current.saveBlock(options),
    screenshot: options => latest.current.screenshot(options),
    getHtml: root => latest.current.getHtml(root),
    getCss: () => readSandboxCss(),
    isReady: () => Boolean(store.query.getNodes().ROOT?.dom),
    waitForFlush: () => sleep(LIMITS.settleAfterMutation),
    resolveFontUrl,
  }), [store])

  const runtime = useMemo(() => createRuntime({ store, resolver, blocks: BLOCKS, helpers }), [store, resolver, helpers])

  useEffect(() => () => runtime.dispose(), [runtime])

  return runtime
}
